// src/components/AssemblyModal.js
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { FiLayers, FiTrash2, FiPlus, FiSearch, FiCheck, FiPackage, FiChevronRight, FiClock } from 'react-icons/fi';
import Modal from './Modal';
import { useApi } from '../hooks/useApi';
import { useModal } from '../context/ModalContext';

// =================================================================================
// STEP 1b: Pick Part from Local Stock (for Assembly)
// =================================================================================
const ComponentSelectModal = ({ isOpen, onClose, items, onSelect, title = "Select Component" }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const filteredItems = useMemo(() => {
        if (!items) return [];
        const lower = searchTerm.toLowerCase();
        return items.filter(i => i.name.toLowerCase().includes(lower) || (i.sku && i.sku.toLowerCase().includes(lower)) || (i.category && i.category.toLowerCase().includes(lower)));
    }, [items, searchTerm]);

    if (!isOpen) return null;
    return (
        <Modal isOpen={isOpen} onClose={onClose} title={title}>
            <div className="search-input-wrapper modal-search-wrapper" style={{ marginBottom: '1rem' }}>
                <FiSearch className="search-icon" />
                <input type="search" className="search-input" placeholder="Search local stock..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} autoFocus />
            </div>
            <div className="selection-list" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {filteredItems.map(item => (
                    <div key={item._id} className="selection-list-item" onClick={() => onSelect(item)} style={{ justifyContent: 'flex-start' }}>
                        <div className="list-view-image-thumbnail-wrapper" style={{ marginRight: '1rem', width: '40px', height: '40px', flexShrink: 0 }}>
                            {item.imageUrl ? <img src={item.imageUrl} alt={item.name} className="list-view-image-thumbnail" /> : <FiPackage size={20} color="var(--gray-400)" />}
                        </div>
                        <div className="item-main-info" style={{ flexGrow: 1 }}>
                            <p className="item-title">{item.name}</p>
                            <p className="item-subtitle">{item.sku ? `SKU: ${item.sku} • ` : ''}{item.category}{item.quantity !== undefined ? ` • In Stock: ${item.quantity}` : ''}</p>
                        </div>
                        <FiChevronRight />
                    </div>
                ))}
            </div>
            <div className="modal-footer"><button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button></div>
        </Modal>
    );
};

// =================================================================================
// STEP 1c: Pick Specific Units (if Component is Serialized)
// =================================================================================
const UnitSelectModal = ({ isOpen, onClose, units, onSelect, selectedPartName }) => {
    const [selectedIds, setSelectedIds] = useState([]);
    if (!isOpen) return null;
    return (
        <Modal isOpen={isOpen} onClose={onClose} title={`Select ${selectedPartName} Units`}>
            <p className="form-group-description">This item is serialized. Pick the specific units to install inside the assembly.</p>
            <div className="selection-list" style={{ maxHeight: '350px', overflowY: 'auto' }}>
                {units.map(unit => {
                    const isSelected = selectedIds.includes(unit._id);
                    return (
                        <div key={unit._id} className={`selection-list-item ${isSelected ? 'selected' : ''}`} onClick={() => setSelectedIds(prev => isSelected ? prev.filter(id => id !== unit._id) : [...prev, unit._id])}>
                            <div className="item-main-info"><p className="item-title">SN: {unit.serialNumber}</p></div>{isSelected && <FiCheck className="selection-checkmark" />}
                        </div>
                    );
                })}
            </div>
            <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
                <button className="btn btn-primary" onClick={() => { onSelect(selectedIds); setSelectedIds([]); }} disabled={selectedIds.length === 0}>Confirm {selectedIds.length} Units</button>
            </div>
        </Modal>
    );
};

// =================================================================================
// MAIN ASSEMBLY BUILDER/VIEWER MODAL
// =================================================================================
const AssemblyModal = ({ isOpen, onClose, parentUnit, masterInventoryItems, onViewChildHistory }) => {
    const [children, setChildren] = useState([]);
    const [localStock, setLocalStock] = useState([]); 
    const [loading, setLoading] = useState(true);
    
    // Sub-modal states
    const [isComponentPickOpen, setIsComponentPickOpen] = useState(false);
    const [isUnitPickOpen, setIsUnitPickOpen] = useState(false);
    
    // Temp data for multi-step pick
    const [tempPart, setTempPart] = useState(null); 
    const [availableUnits, setAvailableUnits] = useState([]); 

    const callApi = useApi();
    const { showConfirm, showAlert } = useModal();

    const fetchData = useCallback(async () => {
        if (!parentUnit) return;
        setLoading(true);
        
        try {
            // 1. Fetch children (what is currently inside this specific assembly unit)
            const childRes = await callApi(`inventory/unit/${parentUnit._id}/children`);
            if (childRes.success && Array.isArray(childRes.data)) {
                setChildren(childRes.data);
            } else {
                setChildren([]);
            }

            // 2. Fetch local stock (what items are available at the same location as the parentUnit)
            const invRes = await callApi('inventory');
            if (invRes.success && Array.isArray(invRes.data)) {
                setLocalStock(invRes.data.filter(i => {
                    // Must be at this location and have stock
                    if (i.ownerId !== parentUnit.ownerId || i.quantity <= 0) return false;
                    
                    // Filter out other assemblies
                    const masterDef = masterInventoryItems?.find(m => m._id === i.masterInventoryItemId);
                    const isAssemblyType = i.isAssembly || masterDef?.itemType === 'Assembly';
                    
                    return !isAssemblyType;
                }));
            } else {
                setLocalStock([]);
            }
        } catch (error) {
            console.error("Failed to fetch assembly data:", error);
            setChildren([]);
            setLocalStock([]);
        }
        
        setLoading(false);
    }, [parentUnit, callApi, masterInventoryItems]);

    useEffect(() => {
        if (isOpen) fetchData();
    }, [isOpen, fetchData]);

    // Safely group children items for a cleaner display
    const groupedChildren = useMemo(() => {
        if (!Array.isArray(children)) return [];
        
        const groups = {};
        children.forEach(c => {
            // EXACT CHECK: If it has ANY serial number, treat it as a tracked, clickable item.
            const isChildSerialized = !!c.serialNumber; 

            if (isChildSerialized) {
                groups[c._id] = { ...c, displayQty: 1, isSerialized: true };
            } else {
                const key = c.masterInventoryItemId || c.name; 
                if (!groups[key]) {
                    groups[key] = { ...c, displayQty: 0, isSerialized: false, childIds: [] };
                }
                groups[key].displayQty += 1;
                groups[key].childIds.push(c._id);
            }
        });
        return Object.values(groups);
    }, [children]);

    // --- Component Adding Logic (Wizard-style) ---
    const handleComponentSelected = async (part) => {
        if (!masterInventoryItems) {
             showAlert("Master catalog items are loading, please try again in a moment.");
             return;
        }

        const masterDef = masterInventoryItems.find(m => m._id === part.masterInventoryItemId);

        if (masterDef?.itemType === 'High Value Equipment' || masterDef?.itemType === 'Assembly') { 
            const res = await callApi(`inventory/${part._id}/units`); 
            if (res.success && Array.isArray(res.data)) {
                setAvailableUnits(res.data.filter(u => u.status === 'Available' && u.ownerId === part.ownerId)); 
                setTempPart(part); 
                setIsComponentPickOpen(false); 
                setIsUnitPickOpen(true); 
            } else {
                showAlert(res.error || "Failed to fetch available units.");
            }
        } else {
            const qtyStr = prompt(`Quantity of ${part.name} to add? (Available: ${part.quantity})`, "1");
            if (qtyStr === null) return; 
            const qty = parseInt(qtyStr, 10);
            
            if (qty > 0 && qty <= part.quantity) {
                const res = await callApi('inventory/assemble', 'POST', {
                    assemblyUnitId: parentUnit._id,
                    sourceInventoryId: part._id, 
                    quantity: qty
                });
                if (res.success) {
                    setIsComponentPickOpen(false); 
                    fetchData(); 
                } else {
                    showAlert(res.error || "Failed to add part to assembly.");
                }
            } else {
                showAlert("Invalid quantity or insufficient stock.");
            }
        }
    };

    const handleUnitsConfirmed = async (unitIds) => {
        if (!tempPart || unitIds.length === 0) return;

        const res = await callApi('inventory/assemble', 'POST', {
            assemblyUnitId: parentUnit._id,
            specificUnitIds: unitIds 
        });

        if (res.success) {
            setIsUnitPickOpen(false); 
            setTempPart(null);
            fetchData(); 
        } else {
            showAlert(res.error || "Failed to add serialized units to assembly.");
        }
    };

    // --- Component Removal Logic ---
    const handleRemovePart = (group) => {
        if (group.isSerialized) {
            // Serialized: Just remove the single selected item
            showConfirm(
                `Remove ${group.name} (SN: ${group.serialNumber}) and return it to stock?`,
                async () => {
                    const res = await callApi('inventory/disassemble', 'POST', {
                        childUnitIds: [group._id],
                        destinationSiteId: parentUnit.ownerId, 
                        destinationType: parentUnit.ownerType
                    });

                    if (res.success) {
                        fetchData();
                    } else {
                        showAlert(res.error || "Failed to remove part from assembly.");
                    }
                },
                'Remove Component',
                true
            );
        } else {
            // Bulk Part: Prompt for the quantity to remove
            const qtyStr = window.prompt(`How many ${group.name} would you like to remove and return to stock? (Max: ${group.displayQty})`, "1");
            
            if (qtyStr === null) return; // User cancelled
            
            const qty = parseInt(qtyStr, 10);

            if (isNaN(qty) || qty <= 0 || qty > group.displayQty) {
                showAlert(`Please enter a valid quantity between 1 and ${group.displayQty}.`);
                return;
            }

            // Slice out the exact number of unit IDs they requested
            const targetIds = group.childIds.slice(0, qty);

            showConfirm(
                `Remove ${qty}x ${group.name} and return to stock?`,
                async () => {
                    const res = await callApi('inventory/disassemble', 'POST', {
                        childUnitIds: targetIds,
                        destinationSiteId: parentUnit.ownerId, 
                        destinationType: parentUnit.ownerType
                    });

                    if (res.success) {
                        fetchData();
                    } else {
                        showAlert(res.error || "Failed to remove parts from assembly.");
                    }
                },
                'Remove Components',
                true
            );
        }
    };

    if (!isOpen || !parentUnit) return null;

    const titleContent = (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <FiLayers /> 
            <span>Assembly: {parentUnit.serialNumber || parentUnit.name}</span>
        </div>
    );

    return (
        <>
            <Modal isOpen={isOpen} onClose={onClose} title={titleContent} size="wide" zIndex={1400}>
                <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                    
                    {/* --- INSTALLED COMPONENTS SECTION --- */}
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                            <h4 style={{ margin: 0, color: 'var(--gray-800)' }}>Installed Components ({children?.length || 0})</h4>
                            <button className="btn btn-secondary btn-sm" onClick={() => setIsComponentPickOpen(true)}>
                                <FiPlus /> Add Component
                            </button>
                        </div>

                        {loading ? (
                            <div className="loading-indicator">Loading components...</div>
                        ) : (
                            <div className="assembly-component-list">
                                {groupedChildren.length > 0 ? groupedChildren.map((child, idx) => (
                                    <div 
                                        key={child._id || idx} 
                                        className={`assembly-item-row ${child.isSerialized ? 'clickable-row' : ''}`} 
                                        onClick={() => {
                                            if (child.isSerialized && onViewChildHistory) {
                                                onViewChildHistory(child);
                                            }
                                        }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem', flexGrow: 1 }}>
                                            <div style={{ 
                                                width: '42px', 
                                                height: '42px', 
                                                backgroundColor: 'var(--gray-100)', 
                                                borderRadius: '8px', 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                justifyContent: 'center',
                                                boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.05)'
                                            }}>
                                                <FiPackage size={20} color="var(--gray-500)" />
                                            </div>
                                            <div className="item-info">
                                                <span className="item-title" style={{ fontSize: '1rem', fontWeight: 600 }}>
                                                    {child.name}
                                                </span>
                                                <span className="item-subtitle" style={{ marginTop: '0.2rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                                    {child.isSerialized ? (
                                                        <span className="detail-tag" style={{ fontSize: '0.75rem' }}>
                                                            SN: {child.serialNumber}
                                                        </span>
                                                    ) : (
                                                        `SKU: ${child.sku || 'N/A'}`
                                                    )}
                                                </span>
                                            </div>
                                        </div>
                                        
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                            <div style={{ 
                                                backgroundColor: 'var(--blue-50)', 
                                                color: 'var(--blue-700)', 
                                                padding: '0.25rem 0.75rem', 
                                                borderRadius: '999px',
                                                fontWeight: '700',
                                                fontSize: '0.9rem'
                                            }}>
                                                x{child.displayQty}
                                            </div>
                                            
                                            {child.isSerialized && (
                                                <button 
                                                    className="btn-icon-only" 
                                                    title="View History" 
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (onViewChildHistory) onViewChildHistory(child);
                                                    }}
                                                >
                                                    <FiClock size={16} />
                                                </button>
                                            )}
                                            
                                            <button 
                                                className="btn-icon-only btn-icon-danger" 
                                                onClick={(e) => { e.stopPropagation(); handleRemovePart(child); }} 
                                                title="Remove Part"
                                            >
                                                <FiTrash2 size={16} />
                                            </button>
                                        </div>
                                    </div>
                                )) : (
                                    <div className="assembly-empty-state">
                                        <FiLayers size={32} color="var(--gray-400)" />
                                        <p style={{ margin: 0 }}>No components installed in this assembly.</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={onClose}>Close</button>
                </div>
            </Modal>

            {/* --- STEP 1b: PICK COMPONENT FROM LOCAL STOCK --- */}
            <ComponentSelectModal isOpen={isComponentPickOpen} onClose={() => setIsComponentPickOpen(false)} items={localStock} onSelect={handleComponentSelected} title="Pick Component from Local Stock" zIndex={1500} />
            
            {/* --- STEP 1c: PICK SPECIFIC UNITS (FOR SERIALIZED COMPONENTS) --- */}
            <UnitSelectModal isOpen={isUnitPickOpen} onClose={() => setIsUnitPickOpen(false)} units={availableUnits} onSelect={handleUnitsConfirmed} selectedPartName={tempPart?.name} zIndex={1600} />
        </>
    );
};

export default AssemblyModal;